import React from 'react';
import { Pencil, Plus, Trash2, Upload, X, Package } from 'lucide-react';

import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';

type InstanceOption = {
  id: string;
  name: string;
  slug: string;
  is_demo: boolean;
};

type SupplierRow = {
  id: string;
  name: string;
  slug: string;
  logo_path: string | null;
  logo_url: string | null;
  material_count: number;
  instance_limited?: boolean;
  enabled_instance_ids?: string[];
};

function SupplierAccessEditor({
  supplier,
  instances,
  disabled,
  onSaved,
  onError,
}: {
  supplier: SupplierRow;
  instances: InstanceOption[];
  disabled: boolean;
  onSaved: (next: SupplierRow, instances: InstanceOption[]) => void;
  onError: (message: string) => void;
}) {
  const [limited, setLimited] = React.useState(Boolean(supplier.instance_limited));
  const [selected, setSelected] = React.useState<Set<string>>(
    () => new Set((supplier.enabled_instance_ids ?? []).map(String)),
  );
  const [query, setQuery] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setLimited(Boolean(supplier.instance_limited));
    setSelected(new Set((supplier.enabled_instance_ids ?? []).map(String)));
    setQuery('');
  }, [supplier]);

  const sorted = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...instances]
      .filter((inst) => {
        if (!q) return true;
        return inst.name.toLowerCase().includes(q) || inst.slug.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (a.is_demo !== b.is_demo) return a.is_demo ? 1 : -1;
        return a.name.localeCompare(b.name, 'de');
      });
  }, [instances, query]);

  const dirty =
    limited !== Boolean(supplier.instance_limited) ||
    (limited &&
      [...selected].sort().join(',') !== [...(supplier.enabled_instance_ids ?? [])].map(String).sort().join(','));

  const save = async () => {
    setSaving(true);
    onError('');
    try {
      const res = await fetch(`/api/admin/suppliers/${encodeURIComponent(supplier.id)}/access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          instance_limited: limited,
          enabled_instance_ids: limited ? [...selected] : [],
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as { error?: string } | null)?.error || 'Speichern fehlgeschlagen.');
      const next = (data as { supplier?: SupplierRow })?.supplier;
      const nextInstances = Array.isArray((data as { instances?: InstanceOption[] })?.instances)
        ? (data as { instances: InstanceOption[] }).instances
        : instances;
      if (!next) throw new Error('Speichern fehlgeschlagen.');
      onSaved(next, nextInstances);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-app-border bg-app-surface-muted px-3 py-3">
      <label className="flex items-start gap-2 text-sm text-app-text">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-app-border-strong text-violet-600"
          checked={limited}
          disabled={disabled || saving}
          onChange={(e) => setLimited(e.target.checked)}
        />
        <span>
          <span className="font-medium">Nur für ausgewählte Instanzen</span>
          <span className="mt-0.5 block text-xs text-app-text-subtle">
            Andere Instanzen sehen den Katalog nicht und können ihn über die drei Punkte nicht selbst einblenden.
          </span>
        </span>
      </label>
      {limited ? (
        <div className="mt-3 space-y-2">
          {instances.length > 8 ? (
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Instanzen suchen…"
              className="w-full rounded-md border border-app-border bg-app-surface px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-violet-500/30"
            />
          ) : null}
          <div className="max-h-48 overflow-y-auto rounded-md border border-app-border bg-app-surface">
            {sorted.length === 0 ? (
              <p className="px-3 py-3 text-xs text-slate-400">Keine Instanzen.</p>
            ) : (
              sorted.map((inst) => (
                <label
                  key={inst.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-app-hover"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-app-border-strong text-violet-600"
                    checked={selected.has(inst.id)}
                    disabled={disabled || saving}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(inst.id);
                        else next.delete(inst.id);
                        return next;
                      });
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate text-app-text">{inst.name}</span>
                  {inst.is_demo ? (
                    <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                      Demo
                    </span>
                  ) : null}
                </label>
              ))
            )}
          </div>
          <p className="text-[11px] text-app-text-subtle">
            {selected.size.toLocaleString('de-DE')} Instanz(en) ausgewählt.
          </p>
        </div>
      ) : null}
      <button
        type="button"
        disabled={disabled || saving || !dirty}
        onClick={() => void save()}
        className="mt-3 inline-flex items-center rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
      >
        {saving ? <LoadingSpinner size="sm" /> : null}
        Sichtbarkeit speichern
      </button>
    </div>
  );
}

export function AdminSuppliersTab() {
  const [items, setItems] = React.useState<SupplierRow[]>([]);
  const [instances, setInstances] = React.useState<InstanceOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');
  const [actionId, setActionId] = React.useState<string | null>(null);
  const [openId, setOpenId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/suppliers?all=1', { credentials: 'include' });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as { error?: string } | null)?.error || 'Großhändler konnten nicht geladen werden.');
      setItems(Array.isArray((data as { items?: SupplierRow[] })?.items) ? (data as { items: SupplierRow[] }).items : []);
      setInstances(
        Array.isArray((data as { instances?: InstanceOption[] })?.instances)
          ? (data as { instances: InstanceOption[] }).instances
          : [],
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const replaceSupplier = (next: SupplierRow) => {
    setItems((prev) => prev.map((s) => (s.id === next.id ? { ...s, ...next } : s)));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/suppliers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as { error?: string } | null)?.error || 'Großhändler konnte nicht angelegt werden.');
      setNewName('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (id: string) => {
    if (!editName.trim()) return;
    setActionId(id);
    setError(null);
    try {
      const res = await fetch(`/api/admin/suppliers/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName.trim() }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as { error?: string } | null)?.error || 'Aktualisierung fehlgeschlagen.');
      setEditingId(null);
      setEditName('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Großhändler wirklich entfernen?')) return;
    setActionId(id);
    setError(null);
    try {
      const res = await fetch(`/api/admin/suppliers/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as { error?: string } | null)?.error || 'Großhändler konnte nicht entfernt werden.');
      if (openId === id) setOpenId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setActionId(null);
    }
  };

  const handleLogoUpload = async (id: string, file: File) => {
    setActionId(id);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/admin/suppliers/${id}/logo`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as { error?: string } | null)?.error || 'Logo-Upload fehlgeschlagen.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setActionId(null);
    }
  };

  const handleLogoDelete = async (id: string) => {
    setActionId(id);
    setError(null);
    try {
      const res = await fetch(`/api/admin/suppliers/${id}/logo`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as { error?: string } | null)?.error || 'Logo konnte nicht entfernt werden.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      <form
        onSubmit={handleCreate}
        className="flex items-center gap-2 p-3 rounded-lg border border-app-border bg-app-surface-muted"
      >
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Neuer Großhändler (z. B. „Nordwest Handel“)"
          className="flex-1 border border-app-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-violet-500/30 outline-none bg-app-surface"
        />
        <button
          type="submit"
          disabled={!newName.trim() || creating}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-violet-600 text-white text-xs font-medium disabled:opacity-50 hover:bg-violet-700"
        >
          {creating ? <LoadingSpinner size="sm" /> : <Plus className="w-3.5 h-3.5" />}
          Anlegen
        </button>
      </form>

      {loading ? (
        <InlineLoadingState label="Laden…" className="py-10" />
      ) : items.length === 0 ? (
        <p className="text-sm text-app-text-subtle text-center py-8">Noch keine Großhändler angelegt.</p>
      ) : (
        <ul className="divide-y divide-app-border border border-app-border rounded-lg overflow-hidden">
          {items.map((s) => {
            const open = openId === s.id;
            const limitedCount = (s.enabled_instance_ids ?? []).length;
            return (
              <li key={s.id} className="bg-app-surface px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <div className="shrink-0 w-9 h-9 flex items-center justify-center rounded bg-app-surface-muted text-app-text-subtle overflow-hidden">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt="" className="w-9 h-9 object-contain" />
                    ) : (
                      <Package className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {editingId === s.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 border border-app-border rounded-md px-2 py-1 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => void handleRename(s.id)}
                          disabled={actionId === s.id}
                          className="px-2 py-1 text-xs rounded bg-violet-600 text-white disabled:opacity-50"
                        >
                          Speichern
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setEditName('');
                          }}
                          className="p-1 rounded hover:bg-app-hover"
                        >
                          <X className="w-4 h-4 text-app-text-subtle" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-app-text truncate">{s.name}</p>
                        <p className="text-xs text-app-text-subtle">
                          {s.material_count.toLocaleString('de-DE')} Artikel · {s.slug}
                          {s.instance_limited
                            ? ` · ${limitedCount.toLocaleString('de-DE')} Instanz(en)`
                            : ''}
                        </p>
                      </div>
                    )}
                  </div>
                  {editingId !== s.id && (
                    <div className="flex items-center gap-1 shrink-0">
                      {s.instance_limited ? (
                        <span className="mr-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 ring-1 ring-inset ring-amber-200">
                          Begrenzt
                        </span>
                      ) : null}
                      <label
                        className="p-1.5 rounded hover:bg-app-hover cursor-pointer text-app-text-subtle"
                        title="Logo hochladen"
                      >
                        <Upload className="w-4 h-4" />
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void handleLogoUpload(s.id, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {s.logo_path && (
                        <button
                          type="button"
                          onClick={() => void handleLogoDelete(s.id)}
                          disabled={actionId === s.id}
                          title="Logo entfernen"
                          className="p-1.5 rounded hover:bg-app-hover text-app-text-subtle disabled:opacity-50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(s.id);
                          setEditName(s.name);
                        }}
                        title="Umbenennen"
                        className="p-1.5 rounded hover:bg-app-hover text-app-text-subtle"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(s.id)}
                        disabled={actionId === s.id || s.material_count > 0}
                        title={s.material_count > 0 ? 'Großhändler hat noch Artikel' : 'Entfernen'}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500 disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setOpenId(open ? null : s.id)}
                        className="rounded-md border border-app-border px-2 py-1 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                      >
                        {open ? 'Schließen' : 'Sichtbarkeit'}
                      </button>
                    </div>
                  )}
                </div>
                {open ? (
                  <SupplierAccessEditor
                    supplier={s}
                    instances={instances}
                    disabled={actionId === s.id}
                    onSaved={(next, nextInstances) => {
                      replaceSupplier(next);
                      setInstances(nextInstances);
                      setError(null);
                    }}
                    onError={(message) => setError(message)}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
